import { z } from 'zod'

import {
  accept,
  isPlayersTurn,
  nextPlayerId,
  reject,
  type ActionContext,
  type GameEngine,
  type PlaySummary,
  type Player,
  type TurnState,
} from '../engine.js'
import type { Rng } from '../rng.js'
import { pickWord, type ReadingBand } from '../words.js'

/**
 * How many wrong guesses a round allows.
 *
 * Ten, and the same number alone or in a room — one constant, so the two can
 * never drift into being different games. That rule is kept for the engines
 * and it matters just as much for the numbers they are tuned with.
 *
 * It was six, which is a fair round on your own and not a fair round shared.
 * A room spends this pool between everybody: with three cousins, six is two
 * mistakes each and a round can be over before the third of them has had a
 * proper go — the opposite of what a room is for. Ten leaves about three each.
 *
 * Not more than ten, because losing has to stay reachable. Daddy never quite
 * catching up is not a race, and a race nobody can lose is decoration.
 */
export const DEFAULT_LIVES = 10

export interface HangmanConfig {
  readonly band: ReadingBand
  readonly lives?: number
  /** Words already seen this sitting, so a session does not repeat itself. */
  readonly exclude?: ReadonlySet<string>
}

export interface HangmanState {
  /** The answer, lower case. Hidden from players until revealed or lost. */
  readonly word: string
  readonly hint: string
  readonly band: ReadingBand
  /** Letters guessed so far, in guess order. An array, not a Set, so it can
   *  be sent over a socket without special handling. */
  readonly guessed: readonly string[]
  readonly wrongGuesses: number
  readonly livesRemaining: number
  readonly status: 'playing' | 'won' | 'lost'
  readonly turn: TurnState
  readonly startedAt: number
  /** The most recent guess, so a room can see who tried what. */
  readonly lastGuess: LastGuess | null
}

export interface LastGuess {
  readonly playerId: string
  readonly letter: string
  readonly correct: boolean
}

export const hangmanAction = z.union([
  z.object({
    type: z.literal('guessLetter'),
    // Exactly one letter. Case is normalised in the reducer, so a phone
    // keyboard's capitals are not a different guess.
    letter: z.string().regex(/^[a-zA-Z]$/),
  }),
  z.object({
    type: z.literal('guessWord'),
    word: z
      .string()
      .regex(/^[a-zA-Z]+$/)
      .max(64),
  }),
])

export type HangmanAction = z.infer<typeof hangmanAction>

/** Which letters of the answer are currently shown. */
export function revealedLetters(state: HangmanState): readonly (string | null)[] {
  const revealAll = state.status !== 'playing'
  return [...state.word].map((letter) =>
    revealAll || state.guessed.includes(letter) ? letter : null,
  )
}

export function isSolved(state: HangmanState): boolean {
  return [...state.word].every((letter) => state.guessed.includes(letter))
}

/** What a player is allowed to know. The answer is withheld until the end. */
export interface HangmanView {
  readonly letters: readonly (string | null)[]
  readonly hint: string
  readonly guessed: readonly string[]
  readonly livesRemaining: number
  readonly status: HangmanState['status']
  readonly currentPlayerId: string | null
  /** Only present once the game is over. */
  readonly answer: string | null
  readonly lastGuess: LastGuess | null
  /**
   * Who is actually in this round.
   *
   * Somebody joining mid-round is seated but not dealt in, so a client can
   * tell the difference between "your turn is coming" and "you are watching
   * this one out".
   */
  readonly roundPlayerIds: readonly string[]
}

/**
 * The race, worked out from the view — one definition for both ways to play.
 *
 * Solo Hangman and the room's host screen draw the same two racers off the
 * same view, and each had worked these out for itself. CLAUDE.md is explicit
 * about why that is not allowed to stand: a rule forked between the solo and
 * multiplayer paths is "the divergence that is the bug you will not find until
 * she is playing with a cousin". Nothing was wrong with either copy — but two
 * copies is how one of them comes to be.
 *
 * They live beside the view rather than in the web app because both are facts
 * about a `HangmanView`, and the package that defines the view is the one that
 * should say what it means.
 */

/**
 * Hoppity's hops: letters she **guessed**, not letters that are showing.
 *
 * Not the same thing, and the difference is why this is a function at all.
 * `revealedLetters` opens the whole word the moment the round stops being
 * `playing`, so counting what is on the board would put Hoppity on the carrot
 * at the end of a round she **lost** — showing her a win she did not get, at
 * the exact moment she is being told she lost.
 */
export function lettersFound(view: HangmanView): number {
  return view.letters.filter((letter) => letter !== null && view.guessed.includes(letter)).length
}

/**
 * The letters she guessed that were in the word.
 *
 * The answer is only there once the round is over, so both halves are checked
 * — the revealed letters during play, and the word itself at the end. The
 * on-screen keyboard colours its keys from this, and Daddy's steps are what is
 * left over.
 */
export function correctGuesses(view: HangmanView): readonly string[] {
  return view.guessed.filter(
    (letter) => view.letters.includes(letter) || (view.answer?.includes(letter) ?? false),
  )
}

/** Daddy's steps: the guesses that were not in the word. */
export function wrongGuesses(view: HangmanView): number {
  return view.guessed.length - correctGuesses(view).length
}

/**
 * How many lives the round was **dealt**, which nothing on the view states.
 *
 * Wrong guesses plus lives left is however many there ever were. The constant
 * would be an assumption that makes the race quietly disagree with the board
 * rather than fail loudly — and the host screen shipped exactly that, six
 * hearts hardcoded, until DOL-89.
 */
export function livesDealt(view: HangmanView): number {
  return wrongGuesses(view) + view.livesRemaining
}

/**
 * Strips the answer out of the state before it leaves the server.
 *
 * Sending the whole state to a browser would put the answer in devtools, and
 * she would work that out sooner than you would like.
 */
export function toView(state: HangmanState): HangmanView {
  return {
    letters: revealedLetters(state),
    hint: state.hint,
    guessed: state.guessed,
    livesRemaining: state.livesRemaining,
    status: state.status,
    currentPlayerId: state.turn.currentPlayerId,
    answer: state.status === 'playing' ? null : state.word,
    lastGuess: state.lastGuess,
    roundPlayerIds: state.turn.players.map((player) => player.id),
  }
}

/**
 * Passes the turn without anybody having guessed.
 *
 * Used when a turn timer runs out. Deliberately not an action: a client that
 * could send this would be able to skip other people's turns, and there is no
 * legitimate reason for one to ask.
 *
 * Running out of time costs no life. Being slow is not the same as being
 * wrong, and an eight-year-old thinking about a word is not stalling.
 */
export function skipTurn(state: HangmanState): HangmanState {
  if (state.status !== 'playing') return state

  return {
    ...state,
    lastGuess: null,
    turn: { ...state.turn, currentPlayerId: nextPlayerId(state.turn) },
  }
}

function settle(state: HangmanState): HangmanState {
  if (isSolved(state))
    return { ...state, status: 'won', turn: { ...state.turn, currentPlayerId: null } }
  if (state.livesRemaining <= 0) {
    return { ...state, status: 'lost', turn: { ...state.turn, currentPlayerId: null } }
  }
  return state
}

export const hangmanEngine: GameEngine<HangmanConfig, HangmanState, HangmanAction> = {
  key: 'hangman',
  actionSchema: hangmanAction,

  createInitialState(config: HangmanConfig, players: readonly Player[], rng: Rng): HangmanState {
    const entry = pickWord(config.band, rng, config.exclude ? { exclude: config.exclude } : {})
    const lives = config.lives ?? DEFAULT_LIVES

    return {
      word: entry.word,
      hint: entry.hint,
      band: config.band,
      guessed: [],
      wrongGuesses: 0,
      livesRemaining: lives,
      status: 'playing',
      turn: { players, currentPlayerId: players[0]?.id ?? null },
      startedAt: 0,
      lastGuess: null,
    }
  },

  reduce(state, action, context: ActionContext) {
    if (state.status !== 'playing') return reject(state, 'game-over')
    if (!isPlayersTurn(state.turn, context.playerId)) return reject(state, 'not-your-turn')

    if (action.type === 'guessWord') {
      const guess = action.word.toLowerCase()

      if (guess === state.word) {
        return accept(
          settle({
            ...state,
            guessed: [...new Set([...state.guessed, ...state.word])],
          }),
        )
      }

      // A wrong whole-word guess costs one life, same as a wrong letter. Any
      // more and nobody would ever risk it; any less and it beats guessing
      // letters.
      return accept(
        settle({
          ...state,
          lastGuess: { playerId: context.playerId, letter: guess, correct: false },
          wrongGuesses: state.wrongGuesses + 1,
          livesRemaining: state.livesRemaining - 1,
          turn: { ...state.turn, currentPlayerId: nextPlayerId(state.turn) },
        }),
      )
    }

    const letter = action.letter.toLowerCase()

    // A repeat costs nothing and does not pass the turn. On a phone this is a
    // misclick, not a mistake, and punishing it teaches exactly the wrong
    // lesson to someone still learning to spell.
    if (state.guessed.includes(letter)) return reject(state, 'already-played')

    const isHit = state.word.includes(letter)
    const guessed = [...state.guessed, letter]

    if (isHit) {
      return accept(
        settle({
          ...state,
          guessed,
          lastGuess: { playerId: context.playerId, letter, correct: true },
          // The turn passes on a hit as well as a miss, so one confident
          // player cannot take the whole game while everyone else watches.
          turn: { ...state.turn, currentPlayerId: nextPlayerId(state.turn) },
        }),
      )
    }

    return accept(
      settle({
        ...state,
        guessed,
        lastGuess: { playerId: context.playerId, letter, correct: false },
        wrongGuesses: state.wrongGuesses + 1,
        livesRemaining: state.livesRemaining - 1,
        turn: { ...state.turn, currentPlayerId: nextPlayerId(state.turn) },
      }),
    )
  },

  isTerminal: (state) => state.status !== 'playing',

  summarise(state, endedAt): PlaySummary {
    const durationMs = Math.max(0, endedAt - state.startedAt)

    return {
      gameKey: 'hangman',
      result: state.status === 'won' ? 'won' : state.status === 'lost' ? 'lost' : 'abandoned',
      durationMs,
      difficulty: state.band,
    }
  },
}
